// What "New script" offers. Godot's Create Script dialog defaults to a
// template with `_ready`/`_process` stubs rather than an empty file, and
// Roblox's new Script comes with a `print("Hello world!")` - a blank file
// is the worst possible first sight of a language nobody has used before.
// Each template here is real, checked HollowScript against this engine's
// own capability catalog (tests/unit/scriptTemplates.test.ts fails the
// build if one stops type-checking), written to show the two things a
// creator must learn first: `uses` declares what a script touches, and
// `effect` marks every call that touches it.

export interface ScriptTemplate {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Suggested file name (made unique by the caller). */
  readonly defaultName: string;
  readonly runOnStart: boolean;
  readonly source: string;
}

export const SCRIPT_TEMPLATES: readonly ScriptTemplate[] = [
  {
    id: 'blank',
    label: 'Blank script',
    description: 'An empty file.',
    defaultName: 'script',
    runOnStart: false,
    source: '',
  },
  {
    id: 'boot',
    label: 'Run on start',
    description: 'Greets the player the moment the game starts.',
    defaultName: 'boot',
    runOnStart: true,
    source: [
      '// Runs when the game starts (Run on start is on).',
      '// `uses` names what this script may touch;',
      '// `effect` marks each call that touches it.',
      'uses print, logic',
      '',
      'effect print("Game started")',
      'effect logic.announce("Welcome!")',
      '',
    ].join('\n'),
  },
  {
    id: 'spawner',
    label: 'Spawn objects',
    description: 'Places a row of crates in the running scene.',
    defaultName: 'spawner',
    runOnStart: false,
    source: [
      '// Spawns five crates in a row, two units apart.',
      'uses print, entities',
      '',
      'constant assetId = "furn_crate"',
      '',
      'for i in range(0, 5) {',
      '    constant x = 2.0 * i',
      '    constant id = effect entities.spawn(assetId, 0, x, 0.0, 0.0)',
      '    effect print("spawned ${id} at x = ${x}")',
      '}',
      '',
    ].join('\n'),
  },
  {
    id: 'score',
    label: 'Score keeper',
    description: 'Adds to a Logic variable and wins the game at ten.',
    defaultName: 'score',
    runOnStart: false,
    source: [
      '// Reads and writes the same variables your Logic rules use.',
      'uses print, variables, logic',
      '',
      'effect variables.add("score", 1.0)',
      'constant score = effect variables.get("score")',
      'effect print("score is now ${score}")',
      '',
      'if score >= 10.0 {',
      '    effect logic.announce("You win!")',
      '    effect logic.winGame()',
      '}',
      '',
    ].join('\n'),
  },
  {
    id: 'input',
    label: 'Read input',
    description: 'Checks which actions the player is holding.',
    defaultName: 'controls',
    runOnStart: false,
    source: [
      '// Reads the shared input state. Actions use the same',
      '// names players can rebind: "moveForward", "interact",',
      '// "primaryAction" and so on.',
      'uses print, input',
      '',
      'constant axis = effect input.moveAxis()',
      'effect print("move axis: ${axis.x}, ${axis.y}")',
      '',
      'if effect input.isActionDown("interact") {',
      '    effect print("interact is held")',
      '}',
      '',
    ].join('\n'),
  },
  {
    id: 'helpers',
    label: 'Helper functions',
    description: 'Functions other scripts import with `import ... from "./helpers"`.',
    defaultName: 'helpers',
    runOnStart: false,
    source: [
      '// Other scripts can use these with:',
      '//   import double, clamp from "./helpers"',
      '',
      'export fn double(x: Int): Int {',
      '    return x * 2',
      '}',
      '',
      'export fn clamp(value: Float, low: Float, high: Float): Float {',
      '    return min(max(value, low), high)',
      '}',
      '',
    ].join('\n'),
  },
];

export function findScriptTemplate(id: string): ScriptTemplate | undefined {
  return SCRIPT_TEMPLATES.find((template) => template.id === id);
}
